import { SeededRandom } from '../core/Random';
import type { Element, SkillDefinition, StatBlock, UnitDefinition } from '../core/types';
import { enemies, getSkill, getUnit, units, type EnemyDefinition } from '../data/content';
import { DamageCalculator } from './DamageCalculator';
import { StatusSystem, type ActiveStatus } from './StatusSystem';

export interface Combatant {
  id: string;
  definitionId: string;
  name: string;
  side: 'ally' | 'enemy';
  role: string;
  element: Element;
  stats: StatBlock;
  hp: number;
  maxHp: number;
  shield: number;
  energy: number;
  alive: boolean;
  statuses: ActiveStatus[];
  cooldowns: Record<string, number>;
  skillIds: string[];
  boss: boolean;
}

export interface CombatEvent {
  type: 'turn' | 'damage' | 'heal' | 'shield' | 'status' | 'defeat' | 'wave' | 'victory' | 'loss' | 'skip';
  sourceId?: string;
  targetId?: string;
  amount?: number;
  label?: string;
  critical?: boolean;
  vfx?: SkillDefinition['vfx'];
}

export interface CombatSnapshot {
  allies: Combatant[];
  enemies: Combatant[];
  wave: number;
  totalWaves: number;
  turn: number;
  finished: boolean;
  victory: boolean;
}

const cloneStats = (stats: StatBlock): StatBlock => ({ ...stats });

export class CombatEngine {
  private readonly rng: SeededRandom;
  private readonly damage: DamageCalculator;
  private readonly status = new StatusSystem();
  private allies: Combatant[] = [];
  private currentEnemies: Combatant[] = [];
  private waveIndex = 0;
  private turn = 0;
  private finished = false;
  private victory = false;

  constructor(private readonly allyIds: string[], private readonly enemyWaves: string[][], seed = Date.now(), private readonly difficulty = 1) {
    if (allyIds.length === 0 || allyIds.length > 5) throw new Error('Combat requires 1–5 allied units');
    if (enemyWaves.length === 0) throw new Error('Combat requires at least one enemy wave');
    this.rng = new SeededRandom(seed);
    this.damage = new DamageCalculator(this.rng);
  }

  start(): CombatEvent[] {
    this.allies = this.allyIds.map((id, index) => this.makeAlly(getUnit(id), index));
    this.waveIndex = 0;
    this.currentEnemies = this.makeWave(this.enemyWaves[0]!);
    return [{ type: 'wave', label: `Wave 1/${this.enemyWaves.length}` }];
  }

  step(manualUltimateId?: string): CombatEvent[] {
    if (this.finished) return [];
    const events: CombatEvent[] = [];
    const actors = [...this.allies, ...this.currentEnemies].filter((item) => item.alive).sort((a, b) => b.stats.speed - a.stats.speed || a.id.localeCompare(b.id));
    this.turn += 1;
    for (const actor of actors) {
      if (!actor.alive || this.finished) continue;
      const foes = actor.side === 'ally' ? this.currentEnemies : this.allies;
      if (!foes.some((item) => item.alive)) { this.resolveWave(events); if (this.finished) break; }
      if (this.status.has(actor.statuses, 'stun') || this.status.has(actor.statuses, 'freeze')) {
        events.push({ type: 'skip', sourceId: actor.id, label: 'Unable to act' });
        this.finishActorTurn(actor, events);
        continue;
      }
      const skill = this.chooseSkill(actor, manualUltimateId === actor.id);
      events.push({ type: 'turn', sourceId: actor.id, label: skill.name, vfx: skill.vfx });
      this.executeSkill(actor, skill, events);
      this.finishActorTurn(actor, events);
      this.checkDefeats(events);
      if (!this.currentEnemies.some((item) => item.alive)) this.resolveWave(events);
      if (!this.allies.some((item) => item.alive)) {
        this.finished = true; this.victory = false; events.push({ type: 'loss', label: 'Formation defeated' });
      }
    }
    return events;
  }

  snapshot(): CombatSnapshot {
    return { allies: this.allies.map(this.copyCombatant), enemies: this.currentEnemies.map(this.copyCombatant), wave: this.waveIndex + 1, totalWaves: this.enemyWaves.length, turn: this.turn, finished: this.finished, victory: this.victory };
  }

  private chooseSkill(actor: Combatant, forceUltimate: boolean): SkillDefinition {
    const skills = actor.skillIds.map(getSkill).filter((skill) => skill.kind !== 'passive');
    const ultimate = skills.find((skill) => skill.kind === 'ultimate');
    if (ultimate && actor.energy >= ultimate.energyCost && (forceUltimate || this.shouldUseUltimate(actor))) return ultimate;
    const ready = skills.filter((skill) => skill.kind === 'skill' && (actor.cooldowns[skill.id] ?? 0) <= 0);
    if (actor.role === 'Healer' && this.lowestHealth(actor.side).hp / this.lowestHealth(actor.side).maxHp < 0.65) return ready.find((skill) => skill.effects.some((effect) => effect.type === 'heal')) ?? skills[0]!;
    return ready[0] ?? skills.find((skill) => skill.kind === 'basic')!;
  }

  private shouldUseUltimate(actor: Combatant): boolean {
    const foes = (actor.side === 'ally' ? this.currentEnemies : this.allies).filter((item) => item.alive);
    if (actor.role === 'Healer') return this.lowestHealth(actor.side).hp / this.lowestHealth(actor.side).maxHp < 0.55;
    return actor.boss || foes.length > 1 || foes[0]!.hp / foes[0]!.maxHp > 0.3;
  }

  private executeSkill(source: Combatant, skill: SkillDefinition, events: CombatEvent[]): void {
    const targets = this.targets(source, skill);
    for (const effect of skill.effects) {
      for (const target of targets) {
        if (!target.alive) continue;
        if (effect.type === 'damage') {
          const attack = cloneStats(source.stats);
          attack.attack *= 1 - this.status.stacks(source.statuses, 'attack-down') * 0.08;
          const defense = cloneStats(target.stats);
          defense.defense *= 1 - this.status.stacks(target.statuses, 'defense-down') * 0.1;
          const result = this.damage.calculate({ attacker: attack, defender: defense, multiplier: effect.multiplier ?? 1, attackerElement: source.element, defenderElement: target.element, vulnerability: this.status.stacks(target.statuses, 'vulnerability') * 0.08 });
          const absorbed = Math.min(target.shield, result.amount);
          target.shield -= absorbed;
          const hpDamage = this.status.has(target.statuses, 'invulnerability') ? 0 : result.amount - absorbed;
          target.hp = Math.max(0, target.hp - hpDamage);
          events.push({ type: 'damage', sourceId: source.id, targetId: target.id, amount: result.amount, critical: result.critical, vfx: skill.vfx });
          source.energy = Math.min(100, source.energy + 12);
        } else if (effect.type === 'heal') {
          const amount = this.damage.heal(source.stats, effect.multiplier ?? 1);
          target.hp = Math.min(target.maxHp, target.hp + amount);
          events.push({ type: 'heal', sourceId: source.id, targetId: target.id, amount, vfx: skill.vfx });
        } else if (effect.type === 'shield') {
          const amount = Math.round(source.stats.attack * (effect.multiplier ?? 0.5) + source.stats.defense * 0.8);
          target.shield = Math.min(target.maxHp, target.shield + amount);
          events.push({ type: 'shield', sourceId: source.id, targetId: target.id, amount, vfx: 'shield' });
        } else if (effect.type === 'status' && effect.statusId && this.rng.next() < (effect.chance ?? 1) + source.stats.effectHit - target.stats.effectResist) {
          this.status.apply(target.statuses, { id: effect.statusId, stacks: effect.stacks ?? 1, remaining: effect.duration ?? 2, sourceId: source.id, value: effect.value ?? source.stats.attack * 0.16 });
          events.push({ type: 'status', sourceId: source.id, targetId: target.id, label: effect.statusId, vfx: skill.vfx });
        } else if (effect.type === 'cleanse') {
          const removed = this.status.cleanse(target.statuses);
          if (removed) events.push({ type: 'status', sourceId: source.id, targetId: target.id, label: `Cleanse ${removed}` });
        } else if (effect.type === 'energy') target.energy = Math.min(100, target.energy + Math.max(0, effect.value ?? 0));
      }
    }
    if (skill.kind === 'ultimate') source.energy = Math.max(0, source.energy - skill.energyCost);
    if (skill.cooldown > 0) source.cooldowns[skill.id] = skill.cooldown;
  }

  private targets(source: Combatant, skill: SkillDefinition): Combatant[] {
    const allies = (source.side === 'ally' ? this.allies : this.currentEnemies).filter((item) => item.alive);
    const foes = (source.side === 'ally' ? this.currentEnemies : this.allies).filter((item) => item.alive);
    if (skill.target === 'self') return [source];
    if (skill.target === 'all-allies') return allies;
    if (skill.target === 'all-enemies') return foes;
    if (skill.target === 'ally-lowest') return [this.lowestHealth(source.side)];
    const taunter = foes.find((item) => this.status.has(item.statuses, 'taunt'));
    return [taunter ?? [...foes].sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0]!];
  }

  private lowestHealth(side: 'ally' | 'enemy'): Combatant {
    const members = (side === 'ally' ? this.allies : this.currentEnemies).filter((item) => item.alive);
    return [...members].sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0]!;
  }

  private finishActorTurn(actor: Combatant, events: CombatEvent[]): void {
    this.status.tick(actor.statuses, 'turn-end', (status) => {
      if (['burn', 'bleed', 'poison', 'frostbite', 'shock'].includes(status.id)) {
        const amount = Math.max(1, Math.round(status.value * status.stacks));
        actor.hp = Math.max(0, actor.hp - amount);
        events.push({ type: 'damage', sourceId: status.sourceId, targetId: actor.id, amount, label: status.id });
      } else if (status.id === 'regeneration') {
        const amount = Math.max(1, Math.round(status.value * status.stacks || actor.maxHp * 0.06));
        actor.hp = Math.min(actor.maxHp, actor.hp + amount);
        events.push({ type: 'heal', targetId: actor.id, amount, label: 'regeneration' });
      }
    });
    Object.keys(actor.cooldowns).forEach((key) => { actor.cooldowns[key] = Math.max(0, actor.cooldowns[key]! - 1); });
    actor.energy = Math.min(100, actor.energy + Math.round(6 + actor.stats.energyRegen * 10));
  }

  private checkDefeats(events: CombatEvent[]): void {
    for (const target of [...this.allies, ...this.currentEnemies]) if (target.alive && target.hp <= 0) { target.alive = false; events.push({ type: 'defeat', targetId: target.id, label: target.name }); }
  }

  private resolveWave(events: CombatEvent[]): void {
    if (this.currentEnemies.some((item) => item.alive)) return;
    if (this.waveIndex + 1 < this.enemyWaves.length) {
      this.waveIndex += 1;
      this.currentEnemies = this.makeWave(this.enemyWaves[this.waveIndex]!);
      events.push({ type: 'wave', label: `Wave ${this.waveIndex + 1}/${this.enemyWaves.length}` });
    } else {
      this.finished = true; this.victory = true; events.push({ type: 'victory', label: 'Stage clear' });
    }
  }

  private makeAlly(def: UnitDefinition, index: number): Combatant {
    const hp = def.baseStats.hp;
    return { id: `ally-${index}-${def.id}`, definitionId: def.id, name: def.name, side: 'ally', role: def.role, element: def.element, stats: cloneStats(def.baseStats), hp, maxHp: hp, shield: 0, energy: 20, alive: true, statuses: [], cooldowns: {}, skillIds: def.skillIds, boss: false };
  }

  private makeWave(ids: string[]): Combatant[] {
    return ids.slice(0, 5).map((id, index) => this.makeEnemy(enemies.find((item) => item.id === id) ?? enemies[0]!, index));
  }

  private makeEnemy(def: EnemyDefinition, index: number): Combatant {
    const p = def.power * this.difficulty;
    const stats: StatBlock = { hp: Math.round(650 * p), attack: Math.round(96 * p), defense: Math.round(52 * p), speed: Math.round(82 + p * 7), critRate: 0.06, critDamage: 1.45, effectHit: 0.08, effectResist: 0.05, healingBonus: 0, energyRegen: 0.08, damageReduction: def.tier === 'boss' ? 0.08 : 0, elementalDamage: 0.05, breakPower: 1 };
    const template = units[(index + def.id.length) % units.length]!;
    return { id: `enemy-${this.waveIndex}-${index}-${def.id}`, definitionId: def.id, name: def.name, side: 'enemy', role: def.role, element: def.element, stats, hp: stats.hp, maxHp: stats.hp, shield: 0, energy: 0, alive: true, statuses: [], cooldowns: {}, skillIds: template.skillIds, boss: def.tier === 'boss' || def.tier === 'raid' || def.tier === 'secret' };
  }

  private copyCombatant(value: Combatant): Combatant { return { ...value, stats: { ...value.stats }, statuses: value.statuses.map((item) => ({ ...item })), cooldowns: { ...value.cooldowns }, skillIds: [...value.skillIds] }; }
}
